import { Controller } from "@hotwired/stimulus"

// Client-side game/status filter for the admin tracks sidebar.
//
// Placed on the outer sidebar container (which wraps both the header and the
// tracks_list turbo-frame). Two chip groups let the admin filter the already-
// rendered list by game and/or build status without a server round-trip.
//
// Coordination with admin-track-groups:
//   When any filter is active (activeCount > 0) the controller sets
//   data-admin-track-groups-filtering-value="true" on the groups root element,
//   which triggers filteringValueChanged() in that controller and forces all
//   groups open. Clearing the filters restores the persisted collapse state
//   by setting it back to "false".
//
// State is in-memory only (resets on full reload — intentional per spec §11).
export default class extends Controller {
  static targets = [
    "panel", // the collapsible filter panel element
    "gameChip", // each game chip button
    "statusChip", // each status chip button
    "count", // badge showing active filter count on the toggle button
    "clear", // "Clear filters" link/button (hidden when count === 0)
    "groupsRoot", // the admin-track-groups controller root element (for filtering flag)
  ]

  static values = {
    game: { type: String, default: "all" },
    status: { type: String, default: "all" },
  }

  connect() {
    this.#updateChipUI()
    this.#applyFilter()
  }

  // Called by Stimulus whenever a groupsRoot target connects to the DOM.
  // This fires after a Turbo frame swap (search re-renders the tracks_list
  // frame), so we re-apply in-memory filter state to the newly rendered list.
  groupsRootTargetConnected() {
    this.#applyFilter()
    this.#syncGroupsFiltering()
  }

  // ── Panel toggle ──────────────────────────────────────────────────────────

  togglePanel() {
    if (!this.hasPanelTarget) return
    // Toggle both: `hidden` (display:none) for closed, `flex` for open. The base
    // class carries only `flex-col gap-3` (layout), never a standalone `flex`,
    // so `hidden` reliably wins the display cascade when closed.
    this.panelTarget.classList.toggle("hidden")
    this.panelTarget.classList.toggle("flex")
  }

  // ── Chip selection ────────────────────────────────────────────────────────

  selectGame(event) {
    this.gameValue = event.currentTarget.dataset.filterGame
  }

  selectStatus(event) {
    this.statusValue = event.currentTarget.dataset.filterStatus
  }

  clearFilters() {
    this.gameValue = "all"
    this.statusValue = "all"
  }

  // ── Value change callbacks ────────────────────────────────────────────────

  gameValueChanged() {
    this.#updateChipUI()
    this.#applyFilter()
    this.#syncGroupsFiltering()
  }

  statusValueChanged() {
    this.#updateChipUI()
    this.#applyFilter()
    this.#syncGroupsFiltering()
  }

  // ── Private ───────────────────────────────────────────────────────────────

  get #activeCount() {
    return (
      (this.gameValue !== "all" ? 1 : 0) + (this.statusValue !== "all" ? 1 : 0)
    )
  }

  #updateChipUI() {
    const count = this.#activeCount

    // Game chips
    this.gameChipTargets.forEach((chip) => {
      const active = chip.dataset.filterGame === this.gameValue
      this.#applyChipState(chip, active)
    })

    // Status chips
    this.statusChipTargets.forEach((chip) => {
      const active = chip.dataset.filterStatus === this.statusValue
      this.#applyChipState(chip, active)
    })

    // Active-count badge on the filter button
    if (this.hasCountTarget) {
      this.countTarget.textContent = count > 0 ? String(count) : ""
      this.countTarget.classList.toggle("hidden", count === 0)
    }

    // "Clear filters" link
    if (this.hasClearTarget) {
      this.clearTarget.classList.toggle("hidden", count === 0)
    }
  }

  // Active chip: vivid-purple tinted background + ring; inactive: surface + muted ring
  #applyChipState(chip, active) {
    if (active) {
      chip.classList.remove(
        "bg-app-surface",
        "ring-app-border",
        "text-app-text-muted",
      )
      chip.classList.add(
        "bg-vivid-purple/15",
        "ring-vivid-purple/55",
        "text-vivid-purple",
      )
    } else {
      chip.classList.remove(
        "bg-vivid-purple/15",
        "ring-vivid-purple/55",
        "text-vivid-purple",
      )
      chip.classList.add(
        "bg-app-surface",
        "ring-app-border",
        "text-app-text-muted",
      )
    }
  }

  #applyFilter() {
    if (!this.hasGroupsRootTarget) return

    const groupsRoot = this.groupsRootTarget
    const game = this.gameValue
    const status = this.statusValue

    // Find all row elements (track cards) within the groups root.
    // Rows have both data-game and data-state attributes.
    const rows = groupsRoot.querySelectorAll("[data-filter-row]")
    rows.forEach((row) => {
      const rowGame = row.dataset.filterGame
      const rowState = row.dataset.filterState
      const gameMatch = game === "all" || rowGame === game
      const statusMatch = status === "all" || rowState === status
      row.classList.toggle("hidden", !(gameMatch && statusMatch))
    })

    // For each group, show it if it has ≥1 visible row; hide if all rows hidden.
    const groups = groupsRoot.querySelectorAll("[data-filter-group]")
    groups.forEach((group) => {
      const groupRows = group.querySelectorAll("[data-filter-row]")
      const hasVisible = Array.from(groupRows).some(
        (r) => !r.classList.contains("hidden"),
      )
      group.classList.toggle("hidden", !hasVisible)
    })
  }

  // Tell the admin-track-groups controller whether filtering is active
  // by writing its Stimulus value attribute on its element.
  #syncGroupsFiltering() {
    if (!this.hasGroupsRootTarget) return
    const filtering = this.#activeCount > 0
    this.groupsRootTarget.dataset.adminTrackGroupsFilteringValue = filtering
      ? "true"
      : "false"
  }
}
